import { type RetailerLogosData } from '@/features/cms/components/CMS/types'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { screenSizes } from '@shipt/design-system-themes'
import styled from 'styled-components'
import { Headline } from '@shipt/design-system-typography'
import { routes, getRetailerUrl } from '@shared/constants/routes'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { useMutationSelectStore } from '@/features/shop/services/ShoppingStore/mutations'
import { useUserDefaultShoppingAddressId, useUserId } from '@/features/account/services/User/hooks'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { useSetGuestStoreCookie } from '@/features/account/services/GuestUser/hooks'
import { type GuestAddressOnSubmitParams } from '@/features/account/hooks/address/useOnSubmitGuestAddress'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicGuestAddressModal } from '@/features/account/components/Modal/GuestAddressModal/DynamicGuestAddressModal'
import { DynamicZipNotCoveredModal } from '@/features/account/components/Modal/ZipNotCoveredModal/DynamicZipNotCoveredModal'
import { NextLink } from '@/features/shared/elements/Link'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { useQueryShoppingStores } from '@/features/shop/services/ShoppingStore/queries'
import { Box } from '@shipt/design-system-layouts'
import { hideScrollBar } from '@/features/shared/styles/effects'

/**
 * This component corresponds to the `retailer_logos` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/retailer_logos
 * - **Production** - https://cms.shipt.com/content-type/pool/retailer_logos
 */
export const RetailerLogos = ({
  content_type_id,
  id,
  data: { heading, heading_hypertext, stores, disable_links },
}: RetailerLogosData) => {
  const { trackCMSElementClicked } = useCMSTrackingEvents()
  const { openDialog } = useHandleDialog()
  const router = useSegwayRouter()
  const userId = useUserId()
  const { mutate: selectStore, isPending } = useMutationSelectStore()
  const defaultShoppingAddressId = useUserDefaultShoppingAddressId()
  const setGuestStoreCookie = useSetGuestStoreCookie()
  const { data: shoppingStores } = useQueryShoppingStores()

  const routeToGlobalHomepage = () => router.push(routes.GLOBAL_HOMEPAGE.url)

  const handleTrackElementClicked = () =>
    trackCMSElementClicked({
      type: 'button',
      content: 'select_delivery_address',
    })

  const handleDeliveryAreaClicked = () => {
    handleTrackElementClicked()
    openDialog(DynamicGuestAddressModal, {
      startChangeStep: true,
      onSubmit: routeToGlobalHomepage,
    })
  }

  const handleOnSubmit = ({
    deliveryStores,
    retailerId,
  }: GuestAddressOnSubmitParams & { retailerId: number }) => {
    const store = deliveryStores.find((s) => s.id === retailerId)
    // navigate to the Retailer Homepage if store is available else navigate to the Global Homepage
    if (store) {
      setGuestStoreCookie(store)
      router.push(getRetailerUrl(store.name))
    } else {
      routeToGlobalHomepage()
    }
  }

  const handleStoreClicked = (retailerId: number) => {
    const message_goal = heading_hypertext ? 'zip_auto_detect_stores' : 'static_marketing_stores'
    trackCMSElementClicked({
      type: 'link',
      content: 'retailer_icon_clicked',
      message_goal,
    })

    const deliveryStores = shoppingStores?.delivery_stores ?? []
    const store = deliveryStores.find((s) => s.id === retailerId)
    if (userId) {
      if (defaultShoppingAddressId && store) {
        return selectStore({
          store: { id: retailerId },
          addressId: defaultShoppingAddressId,
          route: getRetailerUrl(store.name),
        })
      }
      return router.push(routes.GLOBAL_HOMEPAGE.url)
    }
    openDialog(DynamicGuestAddressModal, {
      contentLabel: 'Enter your delivery address',
      startChangeStep: true,
      selectedStoreId: retailerId,
      onError: (zip: string) => openDialog(DynamicZipNotCoveredModal, { zip }),
      onSubmit: (args) => handleOnSubmit({ ...args, retailerId }),
    })
  }

  try {
    const renderHeading = () => {
      if (disable_links) {
        return (
          <Headline as="h2" size="xl" align="center">
            {heading}
            {!!heading_hypertext && <>&nbsp;{heading_hypertext}</>}
          </Headline>
        )
      }

      return (
        <Headline as="h2" size="xl" align="center">
          {heading}
          {!!heading_hypertext && (
            <>
              &nbsp;
              {userId ? (
                <HeadingLink
                  as={NextLink}
                  href={routes.GLOBAL_HOMEPAGE.url}
                  onClick={handleTrackElementClicked}
                >
                  {heading_hypertext}
                </HeadingLink>
              ) : (
                <HeadingLink
                  as={TransparentButton}
                  onClick={handleDeliveryAreaClicked}
                  aria-label="Click to change delivery area"
                >
                  {heading_hypertext}
                </HeadingLink>
              )}
            </>
          )}
        </Headline>
      )
    }

    return (
      <GridLayoutContainer {...getCMSWrapperProps({ content_type_id, id })}>
        <Box marginBottom={{ base: 'xl', md: 'xxl' }}>{renderHeading()}</Box>
        <StoreLogoList className="fullWidth contentAtTablet">
          {stores.map(({ data }) => {
            const { logo, retailer_id } = data
            if (!logo.src) return null
            return (
              <StoreLogoWrapper
                key={logo.src}
                {...(!disable_links && {
                  as: TransparentButton,
                  disabled: isPending,
                  onClick: () => handleStoreClicked(retailer_id),
                })}
              >
                <Image
                  src={logo.src}
                  alt={logo.alt}
                  fill
                  style={{ objectFit: 'contain' }}
                  sizes="114px"
                  quality={100}
                />
              </StoreLogoWrapper>
            )
          })}
        </StoreLogoList>
      </GridLayoutContainer>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const HeadingLink = styled.span`
  text-decoration: underline;
  font-weight: inherit;
  color: inherit;
`

const StoreLogoList = styled.div`
  display: flex;
  gap: 1rem;
  width: fit-content;
  max-width: 100%;
  overflow: auto;
  justify-content: start;
  padding: 0 var(--padding-inline);
  margin: 0 auto;

  @media ${screenSizes.tablet} {
    justify-content: center;
    flex-wrap: wrap;
    width: 100%;
    gap: 1rem;
    padding: 0;
  }

  @media ${screenSizes.smDesktop} {
    gap: 1.5rem;
  }

  ${hideScrollBar}
`

const StoreLogoWrapper = styled.div`
  flex-shrink: 0;
  height: 4rem;
  width: 4rem;
  margin-bottom: 1rem;
  position: relative;

  @media ${screenSizes.tablet} {
    height: 5rem;
    width: 5rem;
  }

  @media ${screenSizes.smDesktop} {
    height: 6.25rem;
    width: 6.25rem;
  }

  @media ${screenSizes.lgDesktop} {
    height: 7.125rem;
    width: 7.125rem;
  }
`
